import {
  activeEntries,
  addDays,
  entryDate,
  monthBounds,
  parseIsoDate,
} from "@/lib/financial/calc";
import type { FinancialAccount, FinancialEntry, PaymentMethod, Period } from "@/lib/financial/types";

export type LedgerKind = "all" | "in" | "out" | "manual";

export const thClass = "px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground";

export const moneyTone = (v: number) => (v < 0 ? "text-state-danger-foreground" : "text-state-success-foreground");

/** Meses antes do anterior são considerados fechados com o contador. */
export function isMonthLocked(year: number, month: number, today: string) {
  const ty = Number(today.slice(0, 4));
  const tm = Number(today.slice(5, 7)) - 1;
  return year * 12 + month < ty * 12 + tm - 1;
}

/** Conta onde o dinheiro cai, pela forma de pagamento. */
export function defaultAccountFor(method: PaymentMethod | null, accounts: FinancialAccount[]): FinancialAccount | undefined {
  const active = accounts.filter((a) => a.is_active);
  const kind = method === "cash" ? "cash" : method === "debit_card" || method === "credit_card" ? "acquirer" : "bank";
  return active.find((a) => a.kind === kind) ?? active.find((a) => a.kind === "bank") ?? active[0];
}

/** Título que nasceu de uma venda: valor e exclusão são da venda; baixar e renegociar seguem livres. */
export function isTitleLinked(t: { sale_payment_id?: string | null }) {
  return !!t.sale_payment_id;
}

/** Lançamento que nasce de título ou de venda: se corrige na origem, não no razão. */
export function isEntryLinked(e: Pick<FinancialEntry, "title_id" | "origin" | "sale_payment_id">) {
  return !!e.title_id || e.origin === "sale" || !!e.sale_payment_id;
}

/** Recebimentos de venda do dia que entraram sem caixa aberto. */
export function salesWithoutSession(entries: FinancialEntry[], day: string) {
  const loose = activeEntries(entries).filter((e) => e.origin === "sale" && !e.cash_session_id && entryDate(e) === day);
  return { count: loose.length, total: loose.reduce((acc, e) => acc + Number(e.amount), 0) };
}

export function filterLedger(entries: FinancialEntry[], period: Period, kind: LedgerKind, q: string, labelOf: (e: FinancialEntry) => string) {
  const query = q.trim().toLowerCase();
  return activeEntries(entries)
    .filter((e) => {
      const d = entryDate(e);
      return d >= period.from && d <= period.to;
    })
    .filter((e) => kind === "all" || (kind === "in" ? Number(e.amount) > 0 : kind === "out" ? Number(e.amount) < 0 : e.origin === "manual"))
    .filter((e) => !query || labelOf(e).toLowerCase().includes(query))
    .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
}

export function periodPresets(today: string) {
  const d = parseIsoDate(today);
  const y = d.getFullYear();
  const m = d.getMonth();
  const thisMonth = monthBounds(y, m);
  const lastMonth = monthBounds(y, m - 1);
  const nextMonth = monthBounds(y, m + 1);
  return [
    { key: "today", from: today, to: today },
    { key: "last7", from: addDays(today, -6), to: today },
    { key: "thisMonth", ...thisMonth },
    { key: "lastMonth", ...lastMonth },
    { key: "next30", from: today, to: addDays(today, 30) },
    { key: "thisAndNext", from: thisMonth.from, to: nextMonth.to },
    { key: "thisYear", from: `${y}-01-01`, to: `${y}-12-31` },
  ];
}
